import { HTMLRewriter } from 'html-rewriter-wasm';
import type { UnstructuredElement } from '@okrapdf/parser-unstructured';
import { Data, Effect } from 'effect';

export class LiveViewHtmlError extends Data.TaggedError('LiveViewHtmlError')<{
  readonly operation: 'sanitize';
  readonly message: string;
  readonly cause: unknown;
}> {}

function htmlFailure(cause: unknown): LiveViewHtmlError {
  return new LiveViewHtmlError({
    operation: 'sanitize',
    message: cause instanceof Error ? cause.message : String(cause),
    cause,
  });
}

const ALLOWED_TAGS = new Set([
  'div',
  'section',
  'header',
  'footer',
  'aside',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'ul',
  'ol',
  'li',
  'dl',
  'dt',
  'dd',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'caption',
  'colgroup',
  'col',
  'figure',
  'figcaption',
  'img',
  'blockquote',
  'pre',
  'code',
  'strong',
  'em',
  'b',
  'i',
  'u',
  's',
  'sub',
  'sup',
  'small',
  'mark',
  'span',
  'br',
  'hr',
  'a',
  'abbr',
  'cite',
  'time',
]);

const DROP_WITH_CONTENTS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'noscript',
  'template',
  'svg',
  'link',
  'meta',
  'base',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'audio',
  'video',
  'source',
  'track',
  'canvas',
]);

const ATTRIBUTES: Record<string, Set<string>> = {
  a: new Set(['href', 'title']),
  img: new Set(['alt', 'width', 'height']),
  th: new Set(['scope', 'colspan', 'rowspan', 'headers']),
  td: new Set(['colspan', 'rowspan', 'headers']),
  col: new Set(['span']),
  colgroup: new Set(['span']),
  time: new Set(['datetime']),
};

const STRUCTURAL_CLASS = /(?:^|\s)(?:Document|Page|Column|Section|Sidebar)(?:\s|$)/i;

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function safeLink(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return (
    normalized.startsWith('https://') ||
    normalized.startsWith('http://') ||
    normalized.startsWith('mailto:') ||
    normalized.startsWith('#')
  );
}

/** Sanitize provider-produced HTML with a scoped parser before it enters a snapshot or SSE frame. */
export function sanitizeProviderHtmlEffect(html: string): Effect.Effect<string, LiveViewHtmlError> {
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();
  let output = '';
  return Effect.acquireUseRelease(
    Effect.try({
      try: () =>
        new HTMLRewriter((chunk: Uint8Array) => {
          output += decoder.decode(chunk, { stream: true });
        }),
      catch: htmlFailure,
    }),
    (rewriter) =>
      Effect.gen(function* () {
        yield* Effect.try({
          try: () =>
            rewriter.on('*', {
              element(element: {
                tagName: string;
                attributes: Iterable<[string, string]>;
                remove(): void;
                removeAndKeepContent(): void;
                removeAttribute(name: string): void;
              }) {
                const tag = element.tagName.toLowerCase();
                if (DROP_WITH_CONTENTS.has(tag)) {
                  element.remove();
                  return;
                }
                // MathML is not executable, but supporting its full attribute/tag surface safely is a separate
                // contract. Unwrap it so formula text survives; the Formula fallback below remains deterministic.
                if (!ALLOWED_TAGS.has(tag)) {
                  element.removeAndKeepContent();
                  return;
                }
                const allowed = ATTRIBUTES[tag] ?? new Set<string>();
                const drop: string[] = [];
                for (const [name, value] of element.attributes) {
                  const lower = name.toLowerCase();
                  if (
                    !allowed.has(lower) ||
                    (tag === 'a' && lower === 'href' && !safeLink(value))
                  ) {
                    drop.push(name);
                  }
                }
                for (const name of drop) element.removeAttribute(name);
              },
              comments(comment: { remove(): void }) {
                comment.remove();
              },
            }),
          catch: htmlFailure,
        });
        return yield* Effect.tryPromise({
          try: async () => {
            await rewriter.write(encoder.encode(html));
            await rewriter.end();
            output += decoder.decode();
            return output.trim();
          },
          catch: htmlFailure,
        });
      }),
    (rewriter) =>
      Effect.sync(() => {
        rewriter.free();
      }),
  );
}

/** Promise compatibility adapter for existing CLI callers. */
export function sanitizeProviderHtml(html: string): Promise<string> {
  return Effect.runPromise(
    sanitizeProviderHtmlEffect(html).pipe(
      Effect.mapError((error) => (error.cause instanceof Error ? error.cause : error)),
    ),
  );
}

function metadata(element: UnstructuredElement): Record<string, unknown> {
  return element.metadata ?? {};
}

function textAsHtml(element: UnstructuredElement): string | undefined {
  const value = metadata(element).text_as_html;
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function isStructuralContainer(element: UnstructuredElement): boolean {
  const html = textAsHtml(element);
  if (!html) return false;
  const className = html.match(/^\s*<[A-Za-z][\w-]*\b[^>]*\bclass\s*=\s*["']([^"']+)["']/i)?.[1];
  return className ? STRUCTURAL_CLASS.test(className) : false;
}

function headingLevel(element: UnstructuredElement): number {
  const raw = metadata(element).category_depth;
  const depth = typeof raw === 'number' && Number.isFinite(raw) ? Math.floor(raw) : 0;
  return Math.max(1, Math.min(6, depth + 1));
}

function fallbackElementHtml(element: UnstructuredElement): string {
  const text = escapeHtml(typeof element.text === 'string' ? element.text.trim() : '');
  if (!text && element.type !== 'PageBreak') return '';
  switch (element.type) {
    case 'Title': {
      const level = headingLevel(element);
      return `<h${level}>${text}</h${level}>`;
    }
    case 'Header':
    case 'Footer':
    case 'PageNumber':
      return `<p><small>${text}</small></p>`;
    case 'CodeSnippet':
      return `<pre><code>${text}</code></pre>`;
    case 'Formula':
      return `<p><code>${text}</code></p>`;
    case 'Image':
    case 'Figure':
      return `<figure><figcaption>${text || 'Unlabelled figure'}</figcaption></figure>`;
    case 'FigureCaption':
      return `<p><small>${text}</small></p>`;
    case 'PageBreak':
      return '<hr>';
    case 'Table':
      return `<pre>${text}</pre>`;
    default:
      return `<p>${text}</p>`;
  }
}

function renderElementEffect(
  element: UnstructuredElement,
): Effect.Effect<string, LiveViewHtmlError> {
  // Formula markup varies across MathML dialects. A text carrier is both deterministic and accessible.
  if (element.type === 'Formula') return Effect.succeed(fallbackElementHtml(element));
  const providerHtml = textAsHtml(element);
  if (providerHtml) {
    return sanitizeProviderHtmlEffect(providerHtml).pipe(
      Effect.map((sanitized) => sanitized || fallbackElementHtml(element)),
    );
  }
  return Effect.succeed(fallbackElementHtml(element));
}

/** Lower one Unstructured page into the Okra live viewer's deterministic page section. */
export function unstructuredPageHtmlEffect(
  pageNumber: number,
  rawElements: UnstructuredElement[],
  secrets: string[] = [],
): Effect.Effect<string, LiveViewHtmlError> {
  return Effect.gen(function* () {
    const nonStructural = rawElements.filter((element) => !isStructuralContainer(element));
    const elements = nonStructural.length ? nonStructural : rawElements;
    const chunks: string[] = [];

    for (let index = 0; index < elements.length; ) {
      const element = elements[index];
      if (element.type === 'ListItem') {
        const items: string[] = [];
        while (index < elements.length && elements[index].type === 'ListItem') {
          const value =
            typeof elements[index].text === 'string' ? elements[index].text!.trim() : '';
          if (value) items.push(`<li>${escapeHtml(value)}</li>`);
          index += 1;
        }
        if (items.length) chunks.push(`<ul>${items.join('')}</ul>`);
        continue;
      }
      const html = yield* renderElementEffect(element);
      if (html) chunks.push(html);
      index += 1;
    }

    const body = chunks.length
      ? chunks.join('\n')
      : '<p><small>No readable content was detected on this page.</small></p>';
    let pageHtml = `<section id="page-${pageNumber}" data-page="${pageNumber}" aria-label="Page ${pageNumber}">${body}</section>`;
    for (const secret of secrets) {
      if (!secret) continue;
      pageHtml = pageHtml
        .split(secret)
        .join('[REDACTED]')
        .split(escapeHtml(secret))
        .join('[REDACTED]');
    }
    return pageHtml;
  });
}

/** Promise compatibility adapter for existing CLI callers. */
export function unstructuredPageHtml(
  pageNumber: number,
  rawElements: UnstructuredElement[],
  secrets: string[] = [],
): Promise<string> {
  return Effect.runPromise(
    unstructuredPageHtmlEffect(pageNumber, rawElements, secrets).pipe(
      Effect.mapError((error) => (error.cause instanceof Error ? error.cause : error)),
    ),
  );
}

export function pageScaffoldHtml(pageNumber: number): string {
  return `<section id="page-${pageNumber}" data-page="${pageNumber}" aria-label="Page ${pageNumber}"><p><small>Waiting for page ${pageNumber}…</small></p></section>`;
}

export function htmlToText(html: string): string {
  return html
    .replace(/<\/?(?:h[1-6]|p|li|tr|figure|figcaption|section|blockquote|pre|hr)\b[^>]*>/gi, '\n')
    .replace(/<\/?(?:td|th)\b[^>]*>/gi, '\t')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
